import { trim } from 'lodash-es';

import { ETemplateFormat } from '@/Pages/Playground/utils/types';
import { getInputVarsFromTemplate } from '@/components/RichTextEditor/utils/RichTextEditor.utils';

import { CUSTOM_TIME_RANGE_VALUE } from '../constants';
import { Attribute, PartitionPair, TimeRange } from '../types';
import { isGenericVar } from './isGenericVar';

export function validateTimeRange(timeRange: TimeRange) {
  const { last_n_hours, start_time, end_time } = timeRange;

  if (last_n_hours !== CUSTOM_TIME_RANGE_VALUE) {
    if (!last_n_hours || last_n_hours <= 0) {
      return 'Hours must be greater than 0';
    }
  } else {
    if (!start_time) {
      return 'Start time is required';
    }
    if (!end_time) {
      return 'End time is required';
    }
    if (start_time >= end_time) {
      return 'End time must be after start time';
    }
  }

  return null;
}

export function validatePartitions(
  partitionPairs: PartitionPair[]
): string | null {
  if (partitionPairs.length === 0) {
    return null;
  }

  // If there is only one partition pair and both key and value are empty, it is valid and has no partitions
  if (
    partitionPairs.length === 1 &&
    !partitionPairs[0].key.trim() &&
    !partitionPairs[0].value.trim()
  ) {
    return null;
  }

  const hasEmptyKeys = partitionPairs.some((pair) => !pair.key.trim());
  const hasEmptyValues = partitionPairs.some((pair) => !pair.value.trim());
  const hasDuplicateKeys =
    new Set(partitionPairs.map((p) => p.key.trim())).size !==
    partitionPairs.length;

  if (hasEmptyKeys) {
    return 'All category keys must be non-empty';
  } else if (hasEmptyValues) {
    return 'All category descriptions must be non-empty';
  } else if (hasDuplicateKeys) {
    return 'Category keys must be unique';
  }

  return null;
}

export function validateName(name: string): string | null {
  if (!name || !name.trim()) {
    return null; // Optional field
  }

  const trimmedName = name.trim();
  if (trimmedName.length > 200) {
    return 'Name must be 200 characters or less';
  }

  return null;
}

export function validateAttributes(attributes: Attribute[]): string | null {
  // If there are no attributes, or there is only one attribute with no name, description, or type, it is valid and has no attributes
  if (
    attributes.length === 0 ||
    (attributes.length === 1 &&
      !attributes[0].name.trim() &&
      !attributes[0].description.trim())
  ) {
    return null;
  }

  if (attributes.some((a) => !a.name || trim(a.name).includes(' '))) {
    return 'All attribute names must be non-empty and cannot contain whitespace';
  }

  if (attributes.some((a) => !trim(a.description))) {
    return 'All attribute descriptions must be non-empty';
  }

  if (attributes.some((a) => !trim(a.type))) {
    return 'All attributes must have a type';
  }

  return null;
}

export function validateSummaryPrompt(
  summaryPrompt: string | undefined
): string | null {
  if (!summaryPrompt || !summaryPrompt.trim()) {
    return 'Summary prompt must not be empty';
  }

  const vars = getInputVarsFromTemplate(
    summaryPrompt,
    ETemplateFormat.MUSTACHE
  );

  if (vars.length === 0) {
    return 'Summary prompt must contain at least one mustache variable (e.g., {{run.inputs}})';
  } else if (vars.some(isGenericVar)) {
    return 'All variables must be assigned a path';
  }

  return null;
}
